import { ComputeBudgetProgram, PublicKey, SYSVAR_SLOT_HASHES_PUBKEY, SystemProgram, Transaction, TransactionInstruction, VersionedTransaction } from '@solana/web3.js'
import { getConnection, getERConnection, isDevnet } from './connection'
import { getWallet, getPublicKey } from './wallet'
import {
  getPlayerStatePDA, getRunStatePDA, getDelegationPDAs,
  DRILL_GAME_PROGRAM_ID, DELEGATION_PROGRAM_ID, MAGIC_PROGRAM_ID, MAGIC_CONTEXT_ID,
} from './pda'

function getEnvErValidator(): PublicKey | null {
  const env = (import.meta as { env?: Record<string, string> }).env
  const key = env?.VITE_ER_VALIDATOR
  if (!key || key.length === 0) return null
  try {
    return new PublicKey(key)
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// On-chain RunState representation
// ---------------------------------------------------------------------------

export interface CargoItem {
  oreIndex: number
  count: number
}

export interface OnChainRunState {
  authority: PublicKey
  seed: Uint8Array
  column: number
  depth: number
  fuel: number
  maxFuel: number
  cargoWeight: number
  maxCargo: number
  cargoCount: number
  cargo: CargoItem[]
  active: boolean
  seedRequested: boolean
  drillFuelMult: number
  luckMult: number
  sellMult: number
}

const DISCRIMINATOR_SIZE = 8

/**
 * Deserialize a raw RunState account buffer.
 */
export function deserializeRunState(data: Uint8Array): OnChainRunState {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength)
  let offset = DISCRIMINATOR_SIZE

  const authority = new PublicKey(data.slice(offset, offset + 32))
  offset += 32

  const seed = new Uint8Array(data.slice(offset, offset + 32))
  offset += 32

  const column = view.getUint8(offset++)

  const depth = view.getUint32(offset, true)
  offset += 4

  const fuel = view.getUint32(offset, true)
  offset += 4

  const maxFuel = view.getUint32(offset, true)
  offset += 4

  const cargoWeight = view.getUint16(offset, true)
  offset += 2

  const maxCargo = view.getUint16(offset, true)
  offset += 2

  const cargoCount = view.getUint8(offset++)

  const cargoLen = view.getUint32(offset, true)
  offset += 4

  const cargo: CargoItem[] = []
  for (let i = 0; i < cargoLen; i++) {
    const oreIndex = view.getUint8(offset++)
    const count = view.getUint8(offset++)
    cargo.push({ oreIndex, count })
  }

  const active = view.getUint8(offset++) !== 0
  const seedRequested = view.getUint8(offset++) !== 0

  const drillFuelMult = view.getUint16(offset, true)
  offset += 2

  const luckMult = view.getUint16(offset, true)
  offset += 2

  const sellMult = view.getUint16(offset, true)
  offset += 2

  return {
    authority, seed, column, depth, fuel, maxFuel,
    cargoWeight, maxCargo, cargoCount, cargo,
    active, seedRequested, drillFuelMult, luckMult, sellMult,
  }
}

/**
 * Fetch the RunState account for the given authority.
 * @param useER - If true, fetch from the ER connection instead of the base layer.
 */
export async function fetchRunState(
  authority: PublicKey,
  useER = false
): Promise<OnChainRunState | null> {
  const connection = useER ? getERConnection() : getConnection()
  const [pda] = getRunStatePDA(authority)
  const accountInfo = await connection.getAccountInfo(pda)
  if (!accountInfo) return null
  return deserializeRunState(accountInfo.data as Uint8Array)
}

/**
 * Check if the RunState account is currently delegated (owned by the delegation program).
 */
export async function isRunStateDelegated(authority: PublicKey): Promise<boolean> {
  const connection = getConnection()
  const [pda] = getRunStatePDA(authority)
  const accountInfo = await connection.getAccountInfo(pda)
  if (!accountInfo) return false
  return accountInfo.owner.equals(DELEGATION_PROGRAM_ID)
}

// ---------------------------------------------------------------------------
// Anchor instruction discriminators
// sha256("global:<instruction_name>")[0..8]
// ---------------------------------------------------------------------------

const DISC_INIT_PLAYER = new Uint8Array([0x72, 0x1b, 0xdb, 0x90, 0x32, 0x0f, 0xe4, 0x42])
const DISC_INIT_RUN = new Uint8Array([0x94, 0x69, 0x79, 0xac, 0xe6, 0xdf, 0x65, 0x2a])
const DISC_PURCHASE_UPGRADE = new Uint8Array([0x46, 0xc5, 0x97, 0xdc, 0x2f, 0x93, 0x7b, 0xcc])
const DISC_START_RUN = new Uint8Array([0x48, 0xd4, 0x01, 0x5b, 0x3d, 0xba, 0x02, 0x34])
const DISC_DRILL_STEP = new Uint8Array([0xc5, 0x88, 0x47, 0x0a, 0xbf, 0x59, 0x3f, 0x4c])
const DISC_FINISH_RUN = new Uint8Array([0x7d, 0x92, 0xf3, 0xd5, 0x38, 0xdc, 0xd6, 0x19])

// sha256("global:delegate_run")[0..8] and sha256("global:undelegate_run")[0..8]
// These will be computed below via the crypto API at module load
let DISC_DELEGATE_RUN: Uint8Array
let DISC_UNDELEGATE_RUN: Uint8Array
let DISC_REQUEST_SEED_VRF: Uint8Array

// Must match ephemeral_vrf_sdk::consts::DEFAULT_EPHEMERAL_QUEUE
const DEFAULT_EPHEMERAL_VRF_QUEUE = new PublicKey('5hBR571xnXppuCPveTrctfTU7tJLSN94nq7kv7FRK5Tc')
// Must match ephemeral_vrf_sdk::consts::VRF_PROGRAM_ID
const VRF_PROGRAM_ID = new PublicKey('Vrf1RNUjXmQGjmQrQLvJHs9SNkvDJEsRVFPkfSQUwGz')

// Pre-compute discriminators
const _discInit = (async () => {
  const enc = new TextEncoder()
  const dBuf = await crypto.subtle.digest('SHA-256', enc.encode('global:delegate_run'))
  DISC_DELEGATE_RUN = new Uint8Array(dBuf).slice(0, 8)
  const uBuf = await crypto.subtle.digest('SHA-256', enc.encode('global:undelegate_run'))
  DISC_UNDELEGATE_RUN = new Uint8Array(uBuf).slice(0, 8)
  const rBuf = await crypto.subtle.digest('SHA-256', enc.encode('global:request_seed_vrf'))
  DISC_REQUEST_SEED_VRF = new Uint8Array(rBuf).slice(0, 8)
})()

/** Build instruction data: discriminator + optional args bytes */
function ixData(disc: Uint8Array, ...args: number[]): Uint8Array {
  const buf = new Uint8Array(disc.length + args.length)
  buf.set(disc)
  for (let i = 0; i < args.length; i++) buf[disc.length + i] = args[i]
  return buf
}

// ---------------------------------------------------------------------------
// Helper: sign and send a transaction
// ---------------------------------------------------------------------------

async function signAndSend(tx: Transaction, useER = false): Promise<string> {
  const wallet = getWallet()
  const pubkey = getPublicKey()
  if (!wallet || !pubkey) throw new Error('Wallet not connected')

  const conn = useER ? getERConnection() : getConnection()
  tx.feePayer = pubkey
  tx.recentBlockhash = (await conn.getLatestBlockhash()).blockhash

  const signed = await wallet.signTransaction(tx)
  try {
    return await conn.sendRawTransaction(signed.serialize())
  } catch (err) {
    const withLogs = err as { getLogs?: () => Promise<string[]> }
    let logged = false
    if (withLogs.getLogs) {
      const logs = await withLogs.getLogs().catch(() => null)
      if (logs && logs.length > 0) {
        console.error('Transaction logs:', logs)
        logged = true
      }
    }
    if (!logged) {
      try {
        if (signed instanceof VersionedTransaction) {
          console.error('Tx (signed) versioned instruction count:', signed.message.compiledInstructions.length)
          console.error('Tx (signed) program ids:', signed.message.staticAccountKeys.map(k => k.toBase58()))
        }
        const sim = signed instanceof VersionedTransaction
          ? await conn.simulateTransaction(signed, { sigVerify: false, replaceRecentBlockhash: true })
          : await conn.simulateTransaction(tx)
        if (sim.value?.logs) console.error('Simulation logs:', sim.value.logs)
      } catch (simErr) {
        console.error('Simulation failed. Instruction summary:', tx.instructions.map(ix => ({
          programId: ix.programId.toBase58(),
          keys: ix.keys?.length ?? 0,
          dataLen: ix.data?.length ?? 0,
        })))
        console.error('Simulation failed to produce logs:', simErr)
      }
    }
    throw err
  }
}

function getVrfIdentityPda(): PublicKey {
  const env = (import.meta as { env?: Record<string, string> }).env
  const override = env?.VITE_VRF_IDENTITY
  if (override && override.length > 0) {
    return new PublicKey(override)
  }
  // Must match ephemeral_vrf_sdk::consts::IDENTITY on-chain.
  const seed = new TextEncoder().encode('identity')
  return PublicKey.findProgramAddressSync([seed], DRILL_GAME_PROGRAM_ID)[0]
}

// ---------------------------------------------------------------------------
// Instruction builders
// ---------------------------------------------------------------------------

/**
 * Initialize the PlayerState PDA. Call once per wallet.
 */
export async function initPlayer(): Promise<string> {
  const pubkey = getPublicKey()
  if (!pubkey) throw new Error('Wallet not connected')

  const [playerStatePDA] = getPlayerStatePDA(pubkey)

  const ix = new TransactionInstruction({
    programId: DRILL_GAME_PROGRAM_ID,
    keys: [
      { pubkey: playerStatePDA, isSigner: false, isWritable: true },
      { pubkey: pubkey, isSigner: true, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    data: ixData(DISC_INIT_PLAYER) as Buffer,
  })

  return signAndSend(new Transaction().add(ix))
}

/**
 * Initialize the RunState PDA. Call once per wallet.
 */
export async function initRun(): Promise<string> {
  const pubkey = getPublicKey()
  if (!pubkey) throw new Error('Wallet not connected')

  const [runStatePDA] = getRunStatePDA(pubkey)

  const ix = new TransactionInstruction({
    programId: DRILL_GAME_PROGRAM_ID,
    keys: [
      { pubkey: runStatePDA, isSigner: false, isWritable: true },
      { pubkey: pubkey, isSigner: true, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    data: ixData(DISC_INIT_RUN) as Buffer,
  })

  return signAndSend(new Transaction().add(ix))
}

/**
 * Purchase an upgrade by index (0=drill, 1=fuelTank, 2=cargo, 3=negotiator, 4=luck).
 */
export async function purchaseUpgrade(upgradeIndex: number): Promise<string> {
  const pubkey = getPublicKey()
  if (!pubkey) throw new Error('Wallet not connected')

  const [playerStatePDA] = getPlayerStatePDA(pubkey)

  const ix = new TransactionInstruction({
    programId: DRILL_GAME_PROGRAM_ID,
    keys: [
      { pubkey: playerStatePDA, isSigner: false, isWritable: true },
      { pubkey: pubkey, isSigner: true, isWritable: false },
    ],
    data: ixData(DISC_PURCHASE_UPGRADE, upgradeIndex) as Buffer,
  })

  return signAndSend(new Transaction().add(ix))
}

/**
 * Start a new drilling run with the given column (0-4).
 */
export async function startRun(column: number): Promise<string> {
  const pubkey = getPublicKey()
  if (!pubkey) throw new Error('Wallet not connected')

  const [playerStatePDA] = getPlayerStatePDA(pubkey)
  const [runStatePDA] = getRunStatePDA(pubkey)

  const ix = new TransactionInstruction({
    programId: DRILL_GAME_PROGRAM_ID,
    keys: [
      { pubkey: playerStatePDA, isSigner: false, isWritable: true },
      { pubkey: runStatePDA, isSigner: false, isWritable: true },
      { pubkey: pubkey, isSigner: true, isWritable: true },
    ],
    data: ixData(DISC_START_RUN, column) as Buffer,
  })

  return signAndSend(new Transaction().add(ix))
}

/**
 * Request VRF randomness from MagicBlock (ER).
 * clientSeed is mixed on-chain with authority for caller_seed.
 */
export async function requestVrfSeed(clientSeed?: number): Promise<string> {
  await _discInit
  if (!isDevnet()) {
    throw new Error('VRF is only supported on devnet')
  }
  const pubkey = getPublicKey()
  if (!pubkey) throw new Error('Wallet not connected')

  const [runStatePDA] = getRunStatePDA(pubkey)
  const oracleQueue = DEFAULT_EPHEMERAL_VRF_QUEUE
  const vrfIdentity = getVrfIdentityPda()
  const seed = (clientSeed ?? crypto.getRandomValues(new Uint8Array(1))[0]) & 0xff

  const ix = new TransactionInstruction({
    programId: DRILL_GAME_PROGRAM_ID,
    keys: [
      // Struct accounts
      { pubkey: pubkey, isSigner: true, isWritable: true }, // payer
      { pubkey: runStatePDA, isSigner: false, isWritable: false }, // run_state
      { pubkey: oracleQueue, isSigner: false, isWritable: true }, // oracle_queue
      // Accounts appended by #[vrf] macro
      { pubkey: vrfIdentity, isSigner: false, isWritable: false }, // program_identity
      { pubkey: VRF_PROGRAM_ID, isSigner: false, isWritable: false }, // vrf_program
      { pubkey: SYSVAR_SLOT_HASHES_PUBKEY, isSigner: false, isWritable: false }, // slot_hashes
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false }, // system_program
    ],
    data: ixData(DISC_REQUEST_SEED_VRF, seed) as Buffer,
  })

  return signAndSend(new Transaction().add(ix), true)
}

/**
 * Wait for VRF callback to set the seed (seed_requested=false).
 */
export async function waitForSeed(authority: PublicKey, maxAttempts = 60, delayMs = 250): Promise<OnChainRunState> {
  for (let i = 0; i < maxAttempts; i++) {
    const runState = await fetchRunState(authority, true)
    if (runState && !runState.seedRequested) return runState
    await new Promise(resolve => setTimeout(resolve, delayMs))
  }
  throw new Error('Timed out waiting for VRF seed')
}

/**
 * Send drill-step instruction (1-50 steps per tx).
 * Uses ER connection in non-localnet mode.
 */
export async function sendDrillSteps(steps: number): Promise<string> {
  const pubkey = getPublicKey()
  if (!pubkey) throw new Error('Wallet not connected')

  const [runStatePDA] = getRunStatePDA(pubkey)

  const ix = new TransactionInstruction({
    programId: DRILL_GAME_PROGRAM_ID,
    keys: [
      { pubkey: runStatePDA, isSigner: false, isWritable: true },
      { pubkey: pubkey, isSigner: true, isWritable: false },
    ],
    data: ixData(DISC_DRILL_STEP, steps) as Buffer,
  })

  const tx = new Transaction()
    .add(ComputeBudgetProgram.setComputeUnitLimit({ units: 400_000 }))
    .add(ix)

  return signAndSend(tx, true)
}

/**
 * Finish the active run -- sell cargo and credit cash.
 */
export async function finishRun(): Promise<string> {
  const pubkey = getPublicKey()
  if (!pubkey) throw new Error('Wallet not connected')

  const [playerStatePDA] = getPlayerStatePDA(pubkey)
  const [runStatePDA] = getRunStatePDA(pubkey)

  const ix = new TransactionInstruction({
    programId: DRILL_GAME_PROGRAM_ID,
    keys: [
      { pubkey: playerStatePDA, isSigner: false, isWritable: true },
      { pubkey: runStatePDA, isSigner: false, isWritable: true },
      { pubkey: pubkey, isSigner: true, isWritable: false },
    ],
    data: ixData(DISC_FINISH_RUN) as Buffer,
  })

  return signAndSend(new Transaction().add(ix), false)
}

/**
 * Delegate the RunState PDA to the ephemeral rollup validator.
 * Sent on the base layer.
 */
export async function delegateRun(): Promise<string> {
  await _discInit
  const pubkey = getPublicKey()
  if (!pubkey) throw new Error('Wallet not connected')

  const [runStatePDA] = getRunStatePDA(pubkey)
  const { bufferPda, delegationRecordPda, delegationMetadataPda } = getDelegationPDAs(runStatePDA)
  const validator = getEnvErValidator()

  // Account order must match the #[delegate] macro expansion:
  // payer, buffer_run_state, delegation_record_run_state, delegation_metadata_run_state,
  // run_state, owner_program, delegation_program, system_program
  const ix = new TransactionInstruction({
    programId: DRILL_GAME_PROGRAM_ID,
    keys: [
      { pubkey: pubkey, isSigner: true, isWritable: false },
      { pubkey: bufferPda, isSigner: false, isWritable: true },
      { pubkey: delegationRecordPda, isSigner: false, isWritable: true },
      { pubkey: delegationMetadataPda, isSigner: false, isWritable: true },
      { pubkey: runStatePDA, isSigner: false, isWritable: true },
      { pubkey: DRILL_GAME_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: DELEGATION_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    data: ixData(DISC_DELEGATE_RUN) as Buffer,
  })

  if (validator) {
    ix.keys.push({ pubkey: validator, isSigner: false, isWritable: false })
  }

  return signAndSend(new Transaction().add(ix), false)
}

/**
 * Commit and undelegate the RunState PDA from the ER back to the base layer.
 * Sent on the ER connection.
 */
export async function undelegateRun(): Promise<string> {
  await _discInit
  const pubkey = getPublicKey()
  if (!pubkey) throw new Error('Wallet not connected')

  const [runStatePDA] = getRunStatePDA(pubkey)

  const ix = new TransactionInstruction({
    programId: DRILL_GAME_PROGRAM_ID,
    keys: [
      { pubkey: pubkey, isSigner: true, isWritable: true },
      { pubkey: runStatePDA, isSigner: false, isWritable: true },
      // magic_program and magic_context auto-added by #[commit] macro
      { pubkey: MAGIC_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: MAGIC_CONTEXT_ID, isSigner: false, isWritable: true },
    ],
    data: ixData(DISC_UNDELEGATE_RUN) as Buffer,
  })

  return signAndSend(new Transaction().add(ix), true)
}

/**
 * Wait for the RunState to be committed back to the base layer after undelegation.
 * Polls the base layer until the account is owned by our program again.
 */
export async function waitForUndelegation(authority: PublicKey, maxAttempts = 30): Promise<void> {
  const connection = getConnection()
  const [pda] = getRunStatePDA(authority)
  for (let i = 0; i < maxAttempts; i++) {
    const info = await connection.getAccountInfo(pda)
    if (info && info.owner.equals(DRILL_GAME_PROGRAM_ID)) {
      return
    }
    await new Promise(resolve => setTimeout(resolve, 1000))
  }
  throw new Error('Timed out waiting for RunState undelegation')
}

/**
 * Wait for the delegated RunState (and program identity PDA) to appear on the ER.
 */
export async function waitForDelegation(authority: PublicKey, maxAttempts = 60, delayMs = 250): Promise<void> {
  const erConn = getERConnection()
  const [runStatePDA] = getRunStatePDA(authority)
  for (let i = 0; i < maxAttempts; i++) {
    const runInfo = await erConn.getAccountInfo(runStatePDA)
    if (runInfo && runInfo.owner.equals(DRILL_GAME_PROGRAM_ID)) return
    await new Promise(resolve => setTimeout(resolve, delayMs))
  }
  throw new Error('Timed out waiting for ER delegation to sync')
}
